import React from "react";
import { useParams, Link, Navigate } from "react-router-dom";
import { Phone, MessageCircle, ArrowLeft, Wrench, ArrowUpRight } from "lucide-react";
import SEO from "@/components/SEO";
import Header from "@/components/sections/Header";
import WhyChooseUs from "@/components/sections/WhyChooseUs";
import Reviews from "@/components/sections/Reviews";
import Faq from "@/components/sections/Faq";
import Footer from "@/components/sections/Footer";
import StickyCallBar from "@/components/StickyCallBar";
import WhatsAppFab from "@/components/WhatsAppFab";
import { getService, SERVICES } from "@/lib/services";
import { PHONE_DISPLAY, PHONE_TEL_HREF, WHATSAPP_HREF } from "@/lib/contact";
import {
  localBusinessSchema, breadcrumbSchema, serviceSchema, faqPageSchema,
} from "@/lib/schemas";
import { SITE_URL } from "@/lib/site";

const HERO_BG =
  "https://images.pexels.com/photos/17720190/pexels-photo-17720190.png?auto=compress&cs=tinysrgb&dpr=2&h=1100&w=1700&fm=webp";

export default function ServicePage() {
  const { slug } = useParams();
  const service = getService(slug);
  if (!service) return <Navigate to="/" replace />;

  const canonical = `${SITE_URL}/${service.slug}`;
  const others = SERVICES.filter((s) => s.slug !== service.slug);

  const jsonLd = [
    localBusinessSchema(),
    breadcrumbSchema([
      { name: "Home", url: `${SITE_URL}/` },
      { name: service.name, url: canonical },
    ]),
    serviceSchema({
      name: service.name,
      description: service.intro,
      url: canonical,
    }),
    faqPageSchema(),
  ];

  return (
    <div className="min-h-screen bg-white text-navy" data-testid={`page-service-${service.slug}`}>
      <SEO
        title={service.title}
        description={service.description}
        canonical={canonical}
        keywords={service.keywords}
        image={`${SITE_URL}/og-image.png`}
        jsonLd={jsonLd}
      />

      <Header />
      <main>
        <section
          data-testid="service-hero"
          className="relative isolate min-h-[78vh] flex items-end md:items-center overflow-hidden bg-navy"
        >
          <img
            src={HERO_BG}
            alt={`${service.name} a Roma H24`}
            width="1700"
            height="1100"
            className="absolute inset-0 -z-10 h-full w-full object-cover object-center opacity-70"
            loading="eager"
            fetchPriority="high"
            decoding="async"
          />
          <div className="absolute inset-0 -z-10 bg-gradient-to-b from-navy/80 via-navy/55 to-navy/95" />
          <div className="absolute inset-0 -z-10 hero-grid opacity-30" />

          <div className="mx-auto max-w-7xl px-4 sm:px-6 pt-28 md:pt-32 pb-14 md:pb-20 w-full">
            <Link
              to="/"
              data-testid="service-back-link"
              className="inline-flex items-center gap-2 text-white/80 hover:text-white text-sm font-semibold"
            >
              <ArrowLeft className="h-4 w-4" /> Torna alla home
            </Link>

            <div className="mt-4 max-w-3xl animate-fade-up">
              <span className="inline-flex items-center gap-2 rounded-full bg-white/10 ring-1 ring-white/20 px-3 py-1.5 text-xs sm:text-sm font-semibold tracking-[0.18em] uppercase text-white/90">
                <Wrench className="h-3.5 w-3.5 text-brand-orange" />
                {service.name}
              </span>

              <h1
                data-testid="service-h1"
                className="mt-5 font-display font-black text-white text-shadow-strong text-4xl sm:text-5xl lg:text-6xl leading-[1.05]"
              >
                {service.h1}
              </h1>

              <p
                data-testid="service-intro"
                className="mt-5 text-white/90 text-base sm:text-lg max-w-2xl leading-relaxed"
              >
                {service.intro}
              </p>

              <div className="mt-7 flex flex-col sm:flex-row gap-3 sm:gap-4">
                <a
                  href={PHONE_TEL_HREF}
                  data-testid="service-call-button"
                  className="inline-flex items-center justify-center gap-3 rounded-2xl bg-brand-orange text-white font-bold px-6 py-4 text-lg sm:text-xl animate-pulse-ring btn-glow-orange hover:brightness-110 hover:-translate-y-0.5 transition"
                >
                  <Phone className="h-6 w-6" />
                  Chiama Ora
                  <span className="font-mono">{PHONE_DISPLAY}</span>
                </a>
                <a
                  href={WHATSAPP_HREF}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="service-whatsapp-button"
                  className="inline-flex items-center justify-center gap-3 rounded-2xl bg-whatsapp text-white font-bold px-6 py-4 text-lg sm:text-xl btn-glow-green hover:brightness-110 hover:-translate-y-0.5 transition"
                >
                  <MessageCircle className="h-6 w-6" />
                  WhatsApp
                </a>
              </div>
            </div>
          </div>
        </section>

        <WhyChooseUs />
        <Reviews />
        <Faq />

        {/* Cross-link to other services */}
        <section
          data-testid="service-internal-links"
          className="py-14 md:py-20 bg-[color:var(--brand-surface)]"
          aria-label="Altri servizi"
        >
          <div className="mx-auto max-w-6xl px-4 sm:px-6">
            <p className="text-brand-orange text-sm font-bold tracking-[0.2em] uppercase">Altri servizi</p>
            <h2 className="mt-3 font-display font-black text-2xl sm:text-3xl text-navy tracking-tight">
              Cosa possiamo fare <span className="text-brand-orange">per te a Roma</span>
            </h2>
            <ul className="mt-6 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2 sm:gap-3">
              {others.map((s) => (
                <li key={s.slug}>
                  <Link
                    to={`/${s.slug}`}
                    data-testid={`service-link-${s.slug}`}
                    className="group flex items-center justify-between gap-2 rounded-xl border border-border bg-white px-4 py-3 hover:border-brand-orange hover:bg-brand-orange/5 transition"
                  >
                    <span className="flex items-center gap-2 font-semibold text-navy text-sm">
                      <Wrench className="h-4 w-4 text-brand-orange" />
                      {s.name}
                    </span>
                    <ArrowUpRight className="h-4 w-4 text-muted-foreground group-hover:text-brand-orange transition" />
                  </Link>
                </li>
              ))}
            </ul>
          </div>
        </section>
      </main>

      <Footer />
      <WhatsAppFab />
      <StickyCallBar />
      <div aria-hidden className="h-20 md:h-0" />
    </div>
  );
}
